import { CognitoUserPool, CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';
import { jwtDecode } from 'jwt-decode';

// Cognito configuration from CloudFormation outputs
const poolData = {
  UserPoolId: 'us-east-1_dgb2yqiiC',
  ClientId: '5jf0ln7eqjvhj5jd3llgnjkhbb'
};

const userPool = new CognitoUserPool(poolData);

class AuthService {
  // Sign up new user
  async signUp(email, password, name) {
    return new Promise((resolve, reject) => {
      const attributeList = [
        {
          Name: 'email',
          Value: email
        },
        {
          Name: 'name', 
          Value: name
        }
      ];

      // Generate a username that's not in email format (required for AliasAttributes)
      // User can still login with email due to AliasAttributes configuration
      const username = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5);

      userPool.signUp(username, password, attributeList, null, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        // Store the username for email verification
        localStorage.setItem('signupUsername', username);
        resolve(result);
      });
    });
  }

  // Confirm sign up with verification code
  async confirmSignUp(email, code) {
    return new Promise((resolve, reject) => {
      // Use the stored username from signup
      const username = localStorage.getItem('signupUsername');
      if (!username) {
        reject(new Error('No signup session found. Please sign up again.'));
        return;
      }

      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool
      });

      cognitoUser.confirmRegistration(code, true, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        // Clear the stored username after successful confirmation
        localStorage.removeItem('signupUsername');
        resolve(result);
      });
    });
  }

  // Sign in user
  async signIn(email, password) {
    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password
      });

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (result) => {
          const accessToken = result.getAccessToken().getJwtToken();
          const idToken = result.getIdToken().getJwtToken();
          
          // Store tokens in localStorage
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('idToken', idToken);
          
          resolve({
            accessToken,
            idToken,
            user: this.getCurrentUser()
          });
        },
        onFailure: (err) => {
          reject(err);
        }
      });
    });
  }

  // Sign out user
  signOut() {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
    
    // Clear tokens from localStorage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('idToken');
    
    // Notify other windows/tabs
    window.dispatchEvent(new Event('auth-logout'));
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = localStorage.getItem('idToken');
    if (!token) return false;

    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      return decoded.exp > currentTime;
    } catch (error) {
      return false;
    }
  }

  // Get current user info from JWT token
  getCurrentUser() {
    const token = localStorage.getItem('idToken');
    if (!token) return null;

    try {
      const decoded = jwtDecode(token);
      return {
        userId: decoded.sub,
        email: decoded.email,
        name: decoded.name || decoded.email
      };
    } catch (error) {
      return null;
    }
  }

  // Get JWT token for API calls
  getAccessToken() {
    return localStorage.getItem('accessToken');
  }

  // Get ID token
  getIdToken() {
    return localStorage.getItem('idToken');
  }
}

export default new AuthService();
